import { useEffect, useMemo, useState } from "react";
import {
  detectEnterpriseFromLocation,
  fetchEnterpriseLogo,
  readCachedEnterpriseLogo,
  writeCachedEnterpriseLogo,
} from "../services/enterpriseLogoService";

// Loads enterprise logo (pre-login) based on:
// 1) subdomain (preferred)
// 2) query param fallback (?tenant=... or ?enterprise_id=...)
// 3) default logo
export default function useEnterpriseLogo({ defaultLogoSrc } = {}) {
  const { tenantKey, enterpriseId } = useMemo(() => detectEnterpriseFromLocation(), []);

  const cached = useMemo(
    () => readCachedEnterpriseLogo({ tenantKey, enterpriseId }),
    [tenantKey, enterpriseId],
  );

  const [logoSrc, setLogoSrc] = useState(() => cached?.logoUrl || defaultLogoSrc);
  const [loading, setLoading] = useState(Boolean(tenantKey || enterpriseId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantKey && !enterpriseId) {
      setLoading(false);
      return;
    }

    const abortController = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await fetchEnterpriseLogo({
          tenantKey,
          enterpriseId,
          etag: cached?.etag || null,
          signal: abortController.signal,
        });

        const nextLogoUrl = result?.logoUrl || null;
        if (!nextLogoUrl) {
          setLoading(false);
          return;
        }

        // Preload image before swapping src to avoid visual flicker.
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = nextLogoUrl;
        });

        if (!abortController.signal.aborted) {
          setLogoSrc(nextLogoUrl);

          const ttlSeconds = Number.parseInt(String(result?.cacheTtlSeconds || ""), 10);
          const ttlMs = Number.isFinite(ttlSeconds) && ttlSeconds > 0 ? ttlSeconds * 1000 : undefined;
          writeCachedEnterpriseLogo({
            tenantKey,
            enterpriseId,
            logoUrl: nextLogoUrl,
            etag: result?.etag || null,
            ttlMs,
          });
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          setError(err);
        }
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    })();

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantKey, enterpriseId]);

  return { logoSrc, tenantKey, enterpriseId, loading, error };
}

